import React, { useState, useEffect } from "react";
import axios from "axios";
import { Card } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { toast } from "sonner";
import { Bell, Repeat, Trash2, Check, X } from "lucide-react";

const API = window.__API || "";

// ─── Wiederkehrende Einsätze ──────────────────────────────────────

export const RecurringView = ({ auftraege }) => {
  const [rules, setRules] = useState([]);
  const [selectedAuftrag, setSelectedAuftrag] = useState("");
  const [wiederholung, setWiederholung] = useState("woechentlich");
  const [intervallTage, setIntervallTage] = useState(7);
  const [enddatum, setEnddatum] = useState("");
  const [busy, setBusy] = useState(false);

  const loadRules = async () => {
    try {
      const res = await axios.get(`${API}/api/recurring/regeln`);
      setRules(res.data);
    } catch (e) { console.error(e); }
  };

  useEffect(() => { loadRules(); }, []);

  const createRecurring = async () => {
    if (!selectedAuftrag) { toast.error("Bitte einen Auftrag auswählen"); return; }
    setBusy(true);
    try {
      const res = await axios.post(`${API}/api/recurring/erstellen`, {
        auftrag_id: selectedAuftrag,
        wiederholung,
        intervall_tage: intervallTage,
        enddatum,
      });
      toast.success(`${res.data.generiert} Einsätze automatisch erstellt!`);
      loadRules();
      setSelectedAuftrag("");
    } catch (e) {
      toast.error(e.response?.data?.detail || "Fehler beim Erstellen");
    }
    setBusy(false);
  };

  const deleteRule = async (id) => {
    try {
      await axios.delete(`${API}/api/recurring/${id}`);
      toast.success("Regel gelöscht");
      loadRules();
    } catch (e) { toast.error("Fehler"); }
  };

  return (
    <div className="space-y-4">
      <Card className="p-4">
        <h3 className="font-semibold text-lg mb-3 flex items-center gap-2">
          <Repeat className="w-5 h-5" /> Wiederkehrenden Einsatz erstellen
        </h3>
        <div className="grid grid-cols-1 md:grid-cols-2 gap-3">
          <div>
            <Label>Auftrag auswählen</Label>
            <select
              className="w-full border rounded px-3 py-2 text-sm"
              value={selectedAuftrag}
              onChange={(e) => setSelectedAuftrag(e.target.value)}
            >
              <option value="">— Auftrag wählen —</option>
              {(auftraege || []).map(a => (
                <option key={a.id} value={a.id}>{a.titel} ({a.datum})</option>
              ))}
            </select>
          </div>
          <div>
            <Label>Wiederholung</Label>
            <select
              className="w-full border rounded px-3 py-2 text-sm"
              value={wiederholung}
              onChange={(e) => setWiederholung(e.target.value)}
            >
              <option value="taeglich">Täglich</option>
              <option value="woechentlich">Wöchentlich</option>
              <option value="monatlich">Monatlich</option>
              <option value="benutzerdefiniert">Benutzerdefiniert</option>
            </select>
          </div>
          {wiederholung === "benutzerdefiniert" && (
            <div>
              <Label>Alle X Tage</Label>
              <Input
                type="number"
                min="1"
                value={intervallTage}
                onChange={(e) => setIntervallTage(parseInt(e.target.value) || 1)}
              />
            </div>
          )}
          <div>
            <Label>Enddatum (optional)</Label>
            <Input type="date" value={enddatum} onChange={(e) => setEnddatum(e.target.value)} />
          </div>
        </div>
        <Button onClick={createRecurring} disabled={busy} className="mt-3">
          <Repeat className="w-4 h-4 mr-1" /> Wiederholung erstellen
        </Button>
      </Card>

      {rules.length > 0 && (
        <Card className="p-4">
          <h3 className="font-semibold mb-2">Aktive Regeln</h3>
          <div className="space-y-2">
            {rules.map(r => (
              <div key={r.id} className="flex items-center justify-between p-2 border rounded text-sm">
                <div>
                  <span className="font-medium">{r.auftrag_titel}</span>
                  <span className="text-gray-500 ml-2">
                    ({r.wiederholung === "taeglich" ? "Täglich" :
                      r.wiederholung === "woechentlich" ? "Wöchentlich" :
                      r.wiederholung === "monatlich" ? "Monatlich" :
                      `Alle ${r.intervall_tage} Tage`})
                  </span>
                  {r.enddatum && <span className="text-gray-400 ml-2">bis {r.enddatum}</span>}
                </div>
                <Button variant="ghost" size="sm" onClick={() => deleteRule(r.id)}>
                  <Trash2 className="w-4 h-4 text-red-500" />
                </Button>
              </div>
            ))}
          </div>
        </Card>
      )}
    </div>
  );
};


// ─── Benachrichtigungen ──────────────────────────────────────────

export const BenachrichtigungenView = ({ mitarbeiter }) => {
  const [notifications, setNotifications] = useState([]);
  const [unread, setUnread] = useState(0);
  const [selectedMa, setSelectedMa] = useState("");
  const [sendForm, setSendForm] = useState(false);
  const [sendData, setSendData] = useState({ empfaenger_ids: [], typ: "neuer_einsatz", titel: "", text: "" });

  const load = async () => {
    try {
      const res = await axios.get(`${API}/api/benachrichtigungen`, { params: selectedMa ? { mitarbeiter_id: selectedMa } : {} });
      setNotifications(res.data);
      const ur = await axios.get(`${API}/api/benachrichtigungen/ungelesen`, { params: selectedMa ? { mitarbeiter_id: selectedMa } : {} });
      setUnread(ur.data.ungelesen);
    } catch (e) { console.error(e); }
  };

  useEffect(() => { load(); }, [selectedMa]);

  const markRead = async (id) => {
    await axios.put(`${API}/api/benachrichtigungen/${id}/gelesen`);
    load();
  };

  const markAllRead = async () => {
    await axios.put(`${API}/api/benachrichtigungen/alle-gelesen`, null, { params: selectedMa ? { mitarbeiter_id: selectedMa } : {} });
    toast.success("Alle als gelesen markiert");
    load();
  };

  const sendNotification = async () => {
    if (!sendData.titel || !sendData.text || sendData.empfaenger_ids.length === 0) {
      toast.error("Bitte alle Felder ausfüllen"); return;
    }
    try {
      await axios.post(`${API}/api/benachrichtigungen/senden`, sendData);
      toast.success("Benachrichtigung gesendet!");
      setSendForm(false);
      setSendData({ empfaenger_ids: [], typ: "neuer_einsatz", titel: "", text: "" });
      load();
    } catch (e) { toast.error("Fehler beim Senden"); }
  };

  const deleteNotif = async (id) => {
    await axios.delete(`${API}/api/benachrichtigungen/${id}`);
    load();
  };

  const typLabels = {
    neuer_einsatz: "Neuer Einsatz",
    einsatz_morgen: "Einsatz morgen",
    einsatz_geaendert: "Einsatz geändert",
    stundenzettel: "Stundenzettel",
  };

  return (
    <div className="space-y-4">
      <Card className="p-4">
        <div className="flex items-center justify-between mb-3">
          <h3 className="font-semibold text-lg flex items-center gap-2">
            <Bell className="w-5 h-5" /> Benachrichtigungen
            {unread > 0 && (
              <span className="bg-red-500 text-white text-xs rounded-full px-2 py-0.5">{unread}</span>
            )}
          </h3>
          <div className="flex gap-2">
            <Button variant="outline" size="sm" onClick={markAllRead}>Alle gelesen</Button>
            <Button size="sm" onClick={() => setSendForm(!sendForm)}>
              <Bell className="w-4 h-4 mr-1" /> Senden
            </Button>
          </div>
        </div>

        {/* Filter by employee */}
        <div className="mb-3">
          <select
            className="border rounded px-3 py-1.5 text-sm"
            value={selectedMa}
            onChange={(e) => setSelectedMa(e.target.value)}
          >
            <option value="">Alle Mitarbeiter</option>
            {(mitarbeiter || []).map(m => (
              <option key={m.id} value={m.id}>{m.vorname} {m.nachname}</option>
            ))}
          </select>
        </div>

        {/* Send form */}
        {sendForm && (
          <div className="border rounded p-3 mb-3 bg-gray-50 space-y-2">
            <div className="grid grid-cols-1 md:grid-cols-2 gap-2">
              <div>
                <Label>Empfänger</Label>
                <select
                  className="w-full border rounded px-3 py-2 text-sm"
                  multiple
                  value={sendData.empfaenger_ids}
                  onChange={(e) => setSendData({...sendData, empfaenger_ids: Array.from(e.target.selectedOptions, o => o.value)})}
                >
                  {(mitarbeiter || []).map(m => (
                    <option key={m.id} value={m.id}>{m.vorname} {m.nachname}</option>
                  ))}
                </select>
                <p className="text-xs text-gray-400 mt-1">Strg+Klick für mehrere</p>
              </div>
              <div>
                <Label>Typ</Label>
                <select
                  className="w-full border rounded px-3 py-2 text-sm"
                  value={sendData.typ}
                  onChange={(e) => setSendData({...sendData, typ: e.target.value})}
                >
                  <option value="neuer_einsatz">Neuer Einsatz</option>
                  <option value="einsatz_morgen">Einsatz morgen</option>
                  <option value="einsatz_geaendert">Einsatz geändert</option>
                  <option value="stundenzettel">Stundenzettel abschließen</option>
                </select>
              </div>
            </div>
            <div>
              <Label>Titel</Label>
              <Input value={sendData.titel} onChange={(e) => setSendData({...sendData, titel: e.target.value})} placeholder="z.B. Neuer Einsatz zugewiesen" />
            </div>
            <div>
              <Label>Nachricht</Label>
              <Input value={sendData.text} onChange={(e) => setSendData({...sendData, text: e.target.value})} placeholder="z.B. Du wurdest dem Einsatz Baustelle Zürich zugewiesen." />
            </div>
            <Button onClick={sendNotification} size="sm">Jetzt senden</Button>
          </div>
        )}

        {/* Notification list */}
        {notifications.length === 0 ? (
          <p className="text-gray-400 text-sm">Keine Benachrichtigungen vorhanden.</p>
        ) : (
          <div className="space-y-2 max-h-96 overflow-y-auto">
            {notifications.map(n => (
              <div key={n.id} className={`flex items-start justify-between p-2 border rounded text-sm ${!n.gelesen ? 'bg-blue-50 border-blue-200' : ''}`}>
                <div className="flex-1">
                  <div className="flex items-center gap-2">
                    <span className="text-xs px-1.5 py-0.5 rounded bg-gray-200">{typLabels[n.typ] || n.typ}</span>
                    {!n.gelesen && <span className="w-2 h-2 rounded-full bg-blue-500"></span>}
                  </div>
                  <p className="font-medium mt-0.5">{n.titel}</p>
                  <p className="text-gray-600">{n.text}</p>
                  <p className="text-xs text-gray-400 mt-0.5">{new Date(n.erstellt_am).toLocaleString("de-DE")}</p>
                </div>
                <div className="flex gap-1 ml-2">
                  {!n.gelesen && (
                    <Button variant="ghost" size="sm" onClick={() => markRead(n.id)} title="Als gelesen markieren">
                      <Check className="w-3 h-3" />
                    </Button>
                  )}
                  <Button variant="ghost" size="sm" onClick={() => deleteNotif(n.id)} title="Löschen">
                    <Trash2 className="w-3 h-3 text-red-400" />
                  </Button>
                </div>
              </div>
            ))}
          </div>
        )}
      </Card>
    </div>
  );
};
